import {
  Providers,
  ProviderState,
  Msal2Provider,
  LoginType,
  IProvider,
} from "@microsoft/mgt";
import { Login, useIsSignedIn } from "@microsoft/mgt-react";
import { getAuth, signInWithCustomToken } from "firebase/auth";

Providers.globalProvider = new Msal2Provider({
  clientId: import.meta.env.VITE_AZURE_APP_CLIENTID,
  authority: "https://login.microsoftonline.com/common",
  redirectUri: import.meta.env.VITE_AZURE_REDIRECT_URI
    ? `${import.meta.env.VITE_AZURE_REDIRECT_URI}/auth-redirect.html`
    : `${window.location.origin}/auth-redirect.html`,
  loginType: LoginType.Popup,
  scopes: [
    "User.Read",
    "Notes.Read",
    "Notes.ReadWrite",
    "Files.Read",
    "Files.ReadWrite",
    "Mail.Read",
    "Calendars.Read",
    "Sites.Read.All",
  ],
});

const microsoftGlobalProvider = Providers.globalProvider;

export { microsoftGlobalProvider, ProviderState };

export const ensureSignedIn = async () => {
  const provider = Providers.globalProvider;

  if (!provider) {
    throw new Error("No global provider found.");
  }

  console.log("ensureSignedIn: Current provider state:", provider.state);

  // Check if already signed in
  if (provider.state === ProviderState.SignedIn) {
    console.log("ensureSignedIn: Already signed in");
    return provider;
  }

  // Standard MGT pattern: just call login and trust it
  console.log("ensureSignedIn: Calling provider.login()");

  if (provider.login) {
    try {
      await provider.login();
      console.log("ensureSignedIn: Login completed, state:", provider.state);
    } catch (error) {
      console.error("ensureSignedIn: Login failed:", error);
      throw error;
    }
  } else {
    throw new Error("Provider does not support login.");
  }

  return provider;
};

export const useIsAuthenticated = () => {
  return useIsSignedIn();
};

export const providerIsSignedIn = (state: ProviderState) => {
  return state === ProviderState.SignedIn;
};

export const getAccessToken = async (provider: IProvider) => {
  return await provider.getAccessToken({
    scopes: [
      "User.Read",
      "Notes.Read",
      "Notes.ReadWrite",
      "Files.Read",
      "Files.ReadWrite",
      "Mail.Read",
      "Calendars.Read",
      "Sites.Read.All",
    ],
  });
};

export const getMicrosoftIdToken = async (): Promise<{
  idToken: string;
  email: string;
}> => {
  const provider: any = Providers.globalProvider;

  if (!provider) {
    throw new Error("No global provider found.");
  }

  // Ensure user is signed in
  if (provider.state !== ProviderState.SignedIn && provider.login) {
    await provider.login();
  }

  // Try to access the underlying MSAL app instance from the MGT provider
  const msalApp =
    provider?.provider?.msal ||
    provider?.publicClientApplication ||
    provider?.msal;

  if (!msalApp) {
    throw new Error("MSAL instance not available from provider.");
  }

  const account =
    msalApp.getActiveAccount?.() ||
    (msalApp.getAllAccounts?.() && msalApp.getAllAccounts()[0]);

  const result = await msalApp.acquireTokenSilent({
    account,
    scopes: ["openid", "profile", "email"],
  });

  if (!result?.idToken) {
    throw new Error("Failed to acquire Microsoft idToken.");
  }
  const email =
    result.account?.username || result.account?.idTokenClaims?.email;
  return { idToken: result.idToken as string, email: email as string };
  //return result.idToken as string;
};
